/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useCallback, forwardRef, useImperativeHandle } from 'react';
import { toast } from 'react-toastify';
import { handleYouTubeVideoUpload } from '../../../../../Creation/Video/VideoUploadHelper';
import {
	isYouTubeUrl,
	processYouTubeUrl,
	prepareVideoForUpload
} from '../EmbedHelpers';
import type { EmbedVideosProps } from '../types';

export interface EmbedVideosRef {
	process: () => Promise<void>;
}

const EmbedVideos = forwardRef<EmbedVideosRef, EmbedVideosProps>(({
	url,
	isProcessing,
	onUploadComplete,
	onError,
	companyId
}, ref) =>
{
	/**
	 * Validates YouTube URL
	 */
	const validateYouTubeUrl = useCallback((inputUrl: string): boolean =>
	{
		// For videos, we specifically check for YouTube URLs
		if (!isYouTubeUrl(inputUrl))
		{
			// More descriptive error for videos
			onError('YouTube URL is required. Ensure the URL is from youtube.com or youtu.be');

			return false;
		}

		return true;
	}, [ onError ]);

	/**
	 * Processes and uploads YouTube video
	 */
	const processAndUploadVideo = useCallback(async (inputUrl: string): Promise<void> =>
	{
		try
		{
			// 1. Validate YouTube URL
			if (!validateYouTubeUrl(inputUrl))
			{
				return;
			}

			// 2. Process YouTube URL
			const processedData = processYouTubeUrl(inputUrl);

			if (!processedData)
			{
				throw new Error('Failed to process YouTube URL');
			}

			// Check if we're dealing with a data URI - important for skipping API upload
			const isDataUri = inputUrl.startsWith('data:');

			// 3. Handle YouTube upload - skip for data URIs (though we don't expect video data URIs)
			if (!isDataUri && companyId)
			{
				const uploadData = prepareVideoForUpload(processedData, companyId);

				if (!uploadData || !uploadData.videoId || !uploadData.thumbnailUrl)
				{
					throw new Error('Invalid YouTube data');
				}

				// Call the YouTube upload function
				const uploadResponse = await handleYouTubeVideoUpload(
					companyId,
					{
						videoUrl     : uploadData.videoUrl,
						videoId      : uploadData.videoId,
						thumbnailUrl : uploadData.thumbnailUrl,
						title        : uploadData.title,
						description  : ''
					}
				);

				// Transform the response to match UploadResult type
				const uploadResult = {
					...processedData,
					url     : uploadData.videoUrl,
					source  : 'embed_link',
					videoId : uploadResponse.videos?.[0]?._id || uploadData.videoId // Use the video ID from response or fallback to original
				};

				// 4. Notify parent of successful processing
				onUploadComplete(uploadResult);
			}
			else
			{
				// If no upload was needed (data URI), just notify of successful processing
				onUploadComplete(processedData);
			}
		}
		catch (uploadError)
		{
			console.error('Error processing YouTube URL:', uploadError);
			const errorMessage = uploadError instanceof Error
				? uploadError.message
				: 'Failed to process YouTube URL. Please try again.';

			onError(errorMessage);
			toast.error('Failed to process YouTube URL. Please try again.');
		}
	}, [ validateYouTubeUrl, companyId, onUploadComplete, onError ]);

	/**
	 * Main processing function called by parent
	 */
	const processVideo = useCallback(async (): Promise<void> =>
	{
		if (isProcessing || !url.trim()) return;

		await processAndUploadVideo(url);
	}, [ url, isProcessing, processAndUploadVideo ]);

	// Expose the process function for parent to call
	useImperativeHandle(ref, () => ({
		process : processVideo
	}));

	// This component doesn't render any UI, it's purely for logic
	// The parent component handles all UI rendering
	return null;
});

EmbedVideos.displayName = 'EmbedVideos';

export default EmbedVideos;